(function () {
    'use strict';

    angular
        .module('core')
        .directive('announcementRotator', announcementRotator);

    announcementRotator.$inject = ['$timeout'];

    function announcementRotator($timeout: ng.ITimeoutService): ng.IDirective {
        const DEFAULT_AUTOPLAY = true;
        const DEFAULT_AUTOPLAY_DURATION = 8000;

        return {
            restrict: 'E',
            replace: true,
            scope: {
                items: '=',
                autoplay: '=?',
                autoplayDuration: '=?'
            },
            templateUrl: 'partials/home/directivetemplate?type=AnnouncementRotator',
            link: function (scope: any): void {
                let autoplayPromise: ng.IPromise<any> | null = null;
                let progressRestartPromise: ng.IPromise<any> | null = null;

                scope.currentIndex = 0;
                scope.isDismissed = false;
                scope.isPaused = false;
                scope.isProgressAnimating = false;
                scope.isTransitionEnabled = true;
                scope.renderItems = [];

                scope.isAutoplayEnabled = getAutoplayEnabled();
                scope.resolvedAutoplayDuration = getAutoplayDuration();

                scope.getItems = function (): any[] {
                    return angular.isArray(scope.renderItems)
                        ? scope.renderItems
                        : [];
                };

                scope.hasItems = function (): boolean {
                    return scope.getItems().length > 0;
                };

                scope.getCurrentItem = function (): any {
                    return scope.getItems()[scope.currentIndex];
                };

                scope.getItemsStyle = function (): any {
                    return {
                        transition: scope.isTransitionEnabled
                            ? 'transform 1s ease-in-out'
                            : 'none',
                        transform: 'translateY(-' + (scope.currentIndex * 100) + '%)'
                    };
                };

                scope.getProgressStyle = function (): any {
                    return {
                        'animation-duration': scope.resolvedAutoplayDuration + 'ms'
                    };
                };

                scope.getPageLabel = function (): string {
                    const items = scope.getItems();

                    if (!items.length) {
                        return '';
                    }

                    return (scope.currentIndex + 1) + '/' + items.length;
                };

                scope.next = function (): void {
                    moveNext();
                    restartAutoplayCycle();
                };

                scope.previous = function (): void {
                    const items = scope.getItems();

                    if (!items.length) {
                        return;
                    }

                    if (scope.currentIndex === 0) {
                        scope.isTransitionEnabled = false;
                        scope.currentIndex = items.length - 1;
                    } else {
                        scope.isTransitionEnabled = true;
                        scope.currentIndex -= 1;
                    }

                    restartAutoplayCycle();
                };

                scope.toggleAutoplay = function (): void {
                    if (!scope.isAutoplayEnabled) {
                        return;
                    }

                    scope.isPaused = !scope.isPaused;

                    if (scope.isPaused) {
                        cancelAutoplay();
                        stopProgress();
                        return;
                    }

                    scheduleAutoplay();
                };

                scope.dismiss = function (): void {
                    scope.isDismissed = true;

                    cancelAutoplay();
                    stopProgress();
                };

                scope.$watchCollection('items', function (newItems: any[]): void {
                    scope.renderItems = angular.isArray(newItems)
                        ? newItems.map(function (item: any): any {
                            return angular.extend({}, item, {
                                rotatorBody: flattenAnnouncementBody(item.body)
                            });
                        })
                        : [];

                    if (scope.currentIndex >= scope.renderItems.length) {
                        scope.currentIndex = 0;
                    }

                    restartAutoplayCycle();
                });

                scope.$watchGroup(
                    ['autoplay', 'autoplayDuration'],
                    function (): void {
                        scope.isAutoplayEnabled = getAutoplayEnabled();
                        scope.resolvedAutoplayDuration = getAutoplayDuration();

                        if (!scope.isAutoplayEnabled) {
                            scope.isPaused = false;
                        }

                        restartAutoplayCycle();
                    }
                );

                scope.$on('$destroy', function (): void {
                    cancelAutoplay();
                    cancelProgressRestart();
                });

                function getAutoplayEnabled(): boolean {
                    return angular.isDefined(scope.autoplay)
                        ? scope.autoplay !== false
                        : DEFAULT_AUTOPLAY;
                }

                function getAutoplayDuration(): number {
                    const duration = Number(scope.autoplayDuration);

                    return isFinite(duration) && duration > 0
                        ? duration
                        : DEFAULT_AUTOPLAY_DURATION;
                }

                function flattenAnnouncementBody(body: string): string {
                    if (!body) {
                        return '';
                    }

                    const container = document.createElement('div');

                    container.innerHTML = body;

                    return (container.textContent || container.innerText || '')
                        .replace(/\s+/g, ' ')
                        .trim();
                }

                function moveNext(): void {
                    const items = scope.getItems();

                    if (!items.length) {
                        return;
                    }

                    if (scope.currentIndex === items.length - 1) {
                        scope.isTransitionEnabled = false;
                        scope.currentIndex = 0;
                        return;
                    }

                    scope.isTransitionEnabled = true;
                    scope.currentIndex += 1;
                }

                function restartAutoplayCycle(): void {
                    cancelAutoplay();

                    if (
                        !scope.isAutoplayEnabled ||
                        scope.isPaused ||
                        scope.isDismissed
                    ) {
                        stopProgress();
                        return;
                    }

                    scheduleAutoplay();
                }

                function scheduleAutoplay(): void {
                    const items = scope.getItems();

                    cancelAutoplay();

                    if (
                        !scope.isAutoplayEnabled ||
                        scope.isPaused ||
                        scope.isDismissed ||
                        items.length < 2
                    ) {
                        stopProgress();
                        return;
                    }

                    restartProgress();

                    autoplayPromise = $timeout(function (): void {
                        autoplayPromise = null;

                        moveNext();
                        scheduleAutoplay();
                    }, scope.resolvedAutoplayDuration);
                }

                function cancelAutoplay(): void {
                    if (!autoplayPromise) {
                        return;
                    }

                    $timeout.cancel(autoplayPromise);
                    autoplayPromise = null;
                }

                function restartProgress(): void {
                    cancelProgressRestart();

                    scope.isProgressAnimating = false;

                    progressRestartPromise = $timeout(function (): void {
                        progressRestartPromise = null;

                        if (
                            scope.isAutoplayEnabled &&
                            !scope.isPaused &&
                            !scope.isDismissed &&
                            scope.getItems().length > 1
                        ) {
                            scope.isProgressAnimating = true;
                        }
                    }, 50);
                }

                function stopProgress(): void {
                    cancelProgressRestart();
                    scope.isProgressAnimating = false;
                }

                function cancelProgressRestart(): void {
                    if (!progressRestartPromise) {
                        return;
                    }

                    $timeout.cancel(progressRestartPromise);
                    progressRestartPromise = null;
                }
            }
        };
    }
})();
